import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { GroupInvitationStatus } from 'multirpc-sdk';

import { AccountPermission } from 'modules/permissions/constants';
import { isSSG } from 'modules/common/utils/isSSG';
import { notificationSlice } from 'domains/notification/store/notificationSlice';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { useBalance } from 'modules/billing/hooks/useBalance';
import { useBlockchainsLoader } from 'hooks/useBlockchainsLoader';
import { useBundlePaymentPlansRequest } from 'domains/account/hooks/useBundlePaymentPlansRequest';
import { useCheckAccountPermission } from 'modules/account/hooks/useCheckAccountPermission';
import { useCheckChangedSignupUserSettingsAndUpdate } from 'hooks/useCheckChangedSignupUserSettingsAndUpdate';
import { useJwtManagerInitializer } from 'domains/jwtToken/hooks/useJwtManagerInitializer';
import { useMyBundlesStatus } from 'modules/billing/hooks/useMyBundlesStatus';
import { useMySubscriptions } from 'domains/account/hooks/useMySubscriptions';
import { useNotifications } from 'modules/notifications/hooks/useNotifications';
import { usePaymentOptions } from 'modules/payments/hooks/usePaymentOptions';
import { useReferrer } from 'modules/referralProgram/hooks/useReferrer';
import { useTeamInvitationsQuery } from 'modules/teams/actions/hooks/useTeamInvitationsQuery';
import { useUIProfileConfig } from 'modules/common/hooks/useUIProfileConfig';
import {
  AVOID_CLOSING_NEW_TEAM_NOTIFICATION_FLAG,
  SUCCESS_TEAM_CREATION_NOTIFICATION_ID,
} from 'modules/teams/constants';
import { store } from 'store';
import { useMyBundles } from 'modules/billing/hooks/useMyBundles';

import { useRedirectToStatisticsForPartners } from './useRedirectToStatisticsForPartners';

const invitationsStatuses = [GroupInvitationStatus.PENDING];

export const useInitialization = () => {
  const { isLoggedIn } = useAuth();

  const { group } = useAccount();

  const dispatch = useDispatch();

  const hasBillingRoleAccess = useCheckAccountPermission({
    permission: AccountPermission.Billing,
  });

  const hasJwtReadRoleAccess = useCheckAccountPermission({
    permission: AccountPermission.JwtManagerRead,
  });

  const skipFetchingBase = isSSG() || !isLoggedIn;

  const skipFetchingBilling = skipFetchingBase || !hasBillingRoleAccess;

  const skipFetchingJwt = skipFetchingBase || !hasJwtReadRoleAccess;

  useBalance();
  useMyBundles();
  useMyBundlesStatus({ skipFetching: skipFetchingBilling });
  useMySubscriptions({ skipFetching: skipFetchingBilling });
  useBundlePaymentPlansRequest({ skipFetching: skipFetchingBilling });

  useUIProfileConfig({ skipFetching: !isLoggedIn });

  useCheckChangedSignupUserSettingsAndUpdate();
  useJwtManagerInitializer({ skipFetching: skipFetchingJwt });

  useBlockchainsLoader();

  usePaymentOptions({ skipFetching: !isLoggedIn });

  useRedirectToStatisticsForPartners();

  useReferrer({ skipFetching: !isLoggedIn });

  useNotifications({
    skipFetching: !isLoggedIn,
    only_unseen: true,
    pollingInterval: 30_000,
    group,
  });

  useTeamInvitationsQuery({
    invitationsStatuses,
    pollingInterval: 300_000,
    skipFetching: !isLoggedIn,
  });

  useEffect(() => {
    const notificationQueueState = store.getState().notifications.queue;

    const isSuccessNotificationActive = Boolean(
      notificationQueueState.find(
        notification =>
          notification.key === SUCCESS_TEAM_CREATION_NOTIFICATION_ID,
      ),
    );

    const shouldAvoidClosingNotification = Boolean(
      localStorage.getItem(AVOID_CLOSING_NEW_TEAM_NOTIFICATION_FLAG),
    );

    const shouldCloseNotification =
      isSuccessNotificationActive &&
      !shouldAvoidClosingNotification &&
      isLoggedIn;

    if (shouldCloseNotification) {
      dispatch(
        notificationSlice.actions.hideNotification(
          SUCCESS_TEAM_CREATION_NOTIFICATION_ID,
        ),
      );
    }
  }, [dispatch, group, isLoggedIn]);
};
